import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ResumeUpload from '@/components/common/ResumeUpload';
import { useAuth } from '@/shared/contexts/AuthContext';
import { formatFileSize, formatLongDate } from '../programs/shared';

export const RESUME_ID = 'sap-resume';
// The file input's id (ResumeUpload names it `${id}-file`), the control to
// focus when the resume is missing.
export const RESUME_INPUT_ID = `${RESUME_ID}-file`;

// What is still open: a resume, only where the program requires one. The
// resume is the one on the student's profile; an application that already
// holds a copy needs nothing more.
export function getErrors(answers, rules, userProfile) {
    const has = answers?.programAnswers?.resume || userProfile?.resume;
    return rules?.resumeRequired && !has
        ? [{ id: RESUME_INPUT_ID, label: 'Resume' }]
        : [];
}

/**
 * The resume, shared by every program. It comes from the student's profile,
 * where it is uploaded once for every application, and the application keeps
 * a copy of it when it is submitted. With none on the profile the student can
 * add one here, which saves it to the profile, so they keep their place.
 * An application that already holds a copy shows that copy and asks for
 * nothing. The resume carries the detail the questionnaire does not collect
 * (courses taken, grades), so a program may require it
 * (`rules.resumeRequired`).
 */
function Resume({ answers, errorIds, context }) {
    const { userProfile, refreshProfile } = useAuth();
    const required = Boolean(context?.programRules?.resumeRequired);
    const invalid = Boolean(errorIds?.has(RESUME_INPUT_ID));
    const stored = answers?.programAnswers?.resume ?? null;
    const profile = userProfile?.resume ?? null;
    // Only a submitted application keeps a copy of its own. A draft is given
    // the profile's resume when it is submitted, so it shows that one, and
    // falls back to a file it already holds only while the profile has none.
    const sent = Boolean(context?.resubmitting);
    const copy = sent ? stored : null;
    const resume = sent ? profile : (profile ?? stored);
    const [status, setStatus] = useState('');
    const lineRef = useRef(null);
    const focusLine = useRef(false);

    // The upload control unmounts once the profile holds the file, so the
    // announcement lives here and focus moves to the line that replaces it.
    useEffect(() => {
        if (focusLine.current && resume) {
            lineRef.current?.focus();
            focusLine.current = false;
        }
    }, [resume]);

    const handleUploaded = (saved) => {
        focusLine.current = true;
        setStatus(`Resume uploaded: ${saved?.fileName ?? ''}`);
        return refreshProfile();
    };

    let line = null;
    if (copy) {
        line = (
            <p className="mb-2" id={RESUME_ID} tabIndex={-1} ref={lineRef}>
                <strong>Resume sent with this application:</strong>{' '}
                <span className="text-break">
                    {copy.fileName} ({formatFileSize(copy.size)})
                </span>
                {copy.uploadedAt &&
                    `, added ${formatLongDate(copy.uploadedAt)}`}
                .
            </p>
        );
    } else if (resume) {
        line = (
            <p className="mb-2" id={RESUME_ID} tabIndex={-1} ref={lineRef}>
                <strong>Your resume:</strong>{' '}
                <span className="text-break">
                    {resume.fileName} ({formatFileSize(resume.size)})
                </span>
                , updated {formatLongDate(resume.uploadedAt)}.
            </p>
        );
    }
    const hasResume = Boolean(line);

    return (
        <div>
            <h2 className="h5 mb-2">Resume</h2>
            <p className="text-muted small">
                Your resume doesn&apos;t need job experience. List the courses
                related to the positions you want, with grades if you like, and
                any tutoring, teaching or leadership you&apos;ve done.
            </p>
            {line}
            {!hasResume && (
                <ResumeUpload
                    id={RESUME_ID}
                    resume={null}
                    onChange={handleUploaded}
                    required={required}
                    invalid={invalid}
                />
            )}
            <div role="status" className="visually-hidden">
                {status}
            </div>
            <p className="small mb-0">
                {copy
                    ? 'This application keeps its own copy, so a change to your profile resume does not alter it. '
                    : resume
                      ? 'Every application uses this resume. '
                      : ''}
                <Link to="/profile" target="_blank" rel="noopener noreferrer">
                    {resume || copy
                        ? 'Change it on your profile'
                        : 'Manage it on your profile'}
                    <span className="visually-hidden">
                        {' '}
                        (opens in a new tab)
                    </span>
                </Link>
                .
            </p>
            {invalid && (
                <div role="alert" className="invalid-feedback d-block">
                    Add your resume to continue.
                </div>
            )}
        </div>
    );
}

export default Resume;
